/**
 * Horizontally scrollable tab bar with fade edge indicators.
 * Replaces overflowing tab bars on mobile (SettingsPage, PresenceDashboard, LeadDetail, etc.)
 */

import { useRef, useState, useEffect, useCallback } from 'react'

interface Tab {
  key: string
  label: string
  icon?: React.ReactNode
  count?: number
}

interface ScrollableTabsProps {
  tabs: Tab[]
  activeKey: string
  onChange: (key: string) => void
  className?: string
  /** Use pill style (rounded bg) instead of underline */
  variant?: 'underline' | 'pill'
}

export default function ScrollableTabs({ tabs, activeKey, onChange, className = '', variant = 'underline' }: ScrollableTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)

  const checkScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setShowLeft(el.scrollLeft > 4)
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
  }, [])

  useEffect(() => {
    checkScroll()
    const el = scrollRef.current
    if (!el) return
    el.addEventListener('scroll', checkScroll, { passive: true })
    const ro = new ResizeObserver(checkScroll)
    ro.observe(el)
    return () => { el.removeEventListener('scroll', checkScroll); ro.disconnect() }
  }, [checkScroll])

  // Scroll active tab into view on mount / change
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const active = el.querySelector('[data-active="true"]') as HTMLElement | null
    if (active) {
      active.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
    }
  }, [activeKey])

  const isPill = variant === 'pill'

  return (
    <div className={`relative ${className}`}>
      {/* Left fade */}
      {showLeft && (
        <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
      )}
      {/* Right fade */}
      {showRight && (
        <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
      )}

      <div
        ref={scrollRef}
        className={`flex overflow-x-auto scrollbar-hide ${
          isPill ? 'gap-1 p-1 bg-gray-100 rounded-lg' : 'gap-0 border-b border-gray-200'
        }`}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {tabs.map((tab) => {
          const isActive = tab.key === activeKey
          return (
            <button
              key={tab.key}
              data-active={isActive}
              onClick={() => onChange(tab.key)}
              className={`flex items-center gap-1.5 whitespace-nowrap text-sm font-medium transition-colors flex-shrink-0 min-h-[44px] ${
                isPill
                  ? `px-3 py-2 rounded-md ${
                      isActive ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`
                  : `px-4 py-2.5 border-b-2 ${
                      isActive
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  isActive ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-200 text-gray-500'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
